import { useQuery } from "@apollo/client";
import { HookFormControlShim, Lens } from "@hookform/lenses";
import { Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  ListSubheader,
  MenuItem,
  SxProps,
  TextFieldProps,
  Theme,
  Typography,
} from "@mui/material";
import { isEqual, keyBy, omit, upperFirst } from "lodash";
import _ from "lodash";
import { DateTime } from "luxon";
import { ReactElement, useCallback, useEffect, useMemo } from "react";
import {
  useController,
  useFieldArray,
  useFormContext,
  useWatch,
  UseFieldArrayReturn,
} from "react-hook-form";

import { STATES, STATES_WITH_AREA_CODES } from "@stw/states";
import {
  AudienceFilterConfig,
  AudienceTargeting,
  AudienceTargetingFieldType,
  AudienceTargetingFilterCategory,
  AudienceTargetingFilterOperation,
  DateFilterKey,
  TARGETING_FILTER_ANY_VALUES,
  TargetingDateFilter,
} from "@stw/yup-validators";

import {
  AudienceTargeting as AudienceTargetingGqlOutput,
  AudienceTargetingFieldType as AudienceTargetingFieldTypeGql,
  AudienceTargetingFilterCategory as AudienceTargetingFilterCategoryGql,
  AudienceTargetingFilterOperation as AudienceTargetingFilterOperationGql,
  CampaignStatus,
  DateFilterKey as DateFilterKeyGql,
  GetAudienceContactFieldsQuery,
  GetAudienceContactFieldsQueryVariables,
  AudienceTargetingInput,
  AudienceContactFieldType,
  ListAudienceSearchFiltersQuery,
  ListCampaignsQuery,
  ListAudienceSearchFiltersQueryVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  GET_AUDIENCE_CONTACT_FIELDS,
  LIST_AUDIENCE_SEARCH_FILTERS,
} from "~frontend/graphql/audience";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { useCampaignNamesById } from "~frontend/routes/subaccounts/audiences/useCampaignNamesById";
import theme, { palette } from "~frontend/theme";
import { generateTargetGroupSentence } from "~frontend/util/generateTargetGroupSentence";

import { AsyncWrapper } from "../AsyncWrapper";
import { LinkButton } from "../Link";
import {
  RhfDatePicker,
  RhfSelect,
  RhfNumericalRange,
  RhfCurrencyField,
  RhfTextField,
  RhfCommaSeparatedTextField,
  RhfZipCodeRadius,
} from "../forms/react-hook-form-components";
import { RhfNumberField } from "../forms/react-hook-form-components/RhfNumberField";
import CheckBoxUnchecked from "../icons/CheckBoxUnchecked";
import {
  TargetingFormValues,
  TargetingFormGroup,
  TargetingFormFilter,
  convertGqlAudienceTargetingToFormValues,
} from "./schema";

// zod enum -> GraphQL enum
function convertFilterCategoryToGraphQL(
  category: AudienceTargetingFilterCategory
): AudienceTargetingFilterCategoryGql {
  switch (category) {
    case AudienceTargetingFilterCategory.Location:
      return AudienceTargetingFilterCategoryGql.Location;
    case AudienceTargetingFilterCategory.DonationHistory:
      return AudienceTargetingFilterCategoryGql.DonationHistory;
    case AudienceTargetingFilterCategory.Engagement:
      return AudienceTargetingFilterCategoryGql.Engagement;
    case AudienceTargetingFilterCategory.CustomField:
      return AudienceTargetingFilterCategoryGql.CustomField;
    case AudienceTargetingFilterCategory.SavedSearch:
      return AudienceTargetingFilterCategoryGql.SavedSearch;
  }
}

function convertFieldTypeToGraphql(
  fieldType: AudienceTargetingFieldType
): AudienceTargetingFieldTypeGql {
  switch (fieldType) {
    case "STATE":
      return AudienceTargetingFieldTypeGql.State;
    case "ZIP":
      return AudienceTargetingFieldTypeGql.Zip;
    case "ZIP_RADIUS":
      return AudienceTargetingFieldTypeGql.ZipRadius;
    case "AREA_CODE":
      return AudienceTargetingFieldTypeGql.AreaCode;
    case "BOOLEAN":
      return AudienceTargetingFieldTypeGql.Boolean;
    case "NUMBER":
      return AudienceTargetingFieldTypeGql.Number;
    case "NUMBER_RANGE":
      return AudienceTargetingFieldTypeGql.NumberRange;
    case "CURRENCY":
      return AudienceTargetingFieldTypeGql.Currency;
    case "CURRENCY_RANGE":
      return AudienceTargetingFieldTypeGql.CurrencyRange;
    case "SAVED_SEARCH":
      return AudienceTargetingFieldTypeGql.SavedSearch;
    case "CAMPAIGN":
      return AudienceTargetingFieldTypeGql.Campaign;
    case "IS_NULL":
      return AudienceTargetingFieldTypeGql.IsNull;
    case "BOOLEAN_FALSE":
      return AudienceTargetingFieldTypeGql.BooleanFalse;
    case "BOOLEAN_TRUE":
      return AudienceTargetingFieldTypeGql.BooleanTrue;
    case "ENUM":
      return AudienceTargetingFieldTypeGql.Enum;
    case "TEXT":
      return AudienceTargetingFieldTypeGql.Text;
  }
}

function convertFilterOperationToGraphql(
  filterOperation: AudienceTargetingFilterOperation
): AudienceTargetingFilterOperationGql {
  switch (filterOperation) {
    case "boolean":
      return AudienceTargetingFilterOperationGql.Boolean;
    case "isInAreaCode":
      return AudienceTargetingFilterOperationGql.IsInAreaCode;
    case "notInAreaCode":
      return AudienceTargetingFilterOperationGql.NotInAreaCode;
    case "isInState":
      return AudienceTargetingFilterOperationGql.IsInState;
    case "isInCustomState":
      return AudienceTargetingFilterOperationGql.IsInCustomState;
    case "notInState":
      return AudienceTargetingFilterOperationGql.NotInState;
    case "notInCustomState":
      return AudienceTargetingFilterOperationGql.NotInCustomState;
    case "stateIsNull":
      return AudienceTargetingFilterOperationGql.StateIsNull;
    case "isInZip":
      return AudienceTargetingFilterOperationGql.IsInZip;
    case "isInCustomZip":
      return AudienceTargetingFilterOperationGql.IsInCustomZip;
    case "notInZip":
      return AudienceTargetingFilterOperationGql.NotInZip;
    case "notInCustomZip":
      return AudienceTargetingFilterOperationGql.NotInCustomZip;
    case "isInZipRadius":
      return AudienceTargetingFilterOperationGql.IsInZipRadius;
    case "isInCustomZipRadius":
      return AudienceTargetingFilterOperationGql.IsInCustomZipRadius;
    case "notInZipRadius":
      return AudienceTargetingFilterOperationGql.NotInZipRadius;
    case "notInCustomZipRadius":
      return AudienceTargetingFilterOperationGql.NotInCustomZipRadius;
    case "zipIsNull":
      return AudienceTargetingFilterOperationGql.ZipIsNull;
    case "contains":
      return AudienceTargetingFilterOperationGql.Contains;
    case "notContains":
      return AudienceTargetingFilterOperationGql.NotContains;
    case "matches":
      return AudienceTargetingFilterOperationGql.Matches;
    case "equals":
      return AudienceTargetingFilterOperationGql.Equals;
    case "notEquals":
      return AudienceTargetingFilterOperationGql.NotEquals;
    case "customFalse":
      return AudienceTargetingFilterOperationGql.CustomFalse;
    case "customTrue":
      return AudienceTargetingFilterOperationGql.CustomTrue;
    case "customIsNull":
      return AudienceTargetingFilterOperationGql.CustomIsNull;
    case "between":
      return AudienceTargetingFilterOperationGql.Between;
    case "notBetween":
      return AudienceTargetingFilterOperationGql.NotBetween;
    case "oneOf":
      return AudienceTargetingFilterOperationGql.OneOf;
    case "notOneOf":
      return AudienceTargetingFilterOperationGql.NotOneOf;
    case "gt":
      return AudienceTargetingFilterOperationGql.Gt;
    case "lt":
      return AudienceTargetingFilterOperationGql.Lt;
    case "isNull":
      return AudienceTargetingFilterOperationGql.IsNull;
    case "isInSavedSearch":
      return AudienceTargetingFilterOperationGql.IsInSavedSearch;
    case "totalDonationGt":
      return AudienceTargetingFilterOperationGql.TotalDonationGt;
    case "totalDonationLt":
      return AudienceTargetingFilterOperationGql.TotalDonationLt;
    case "totalDonationBetween":
      return AudienceTargetingFilterOperationGql.TotalDonationBetween;
    case "isRecurringDonor":
      return AudienceTargetingFilterOperationGql.IsRecurringDonor;
    case "notRecurringDonor":
      return AudienceTargetingFilterOperationGql.NotRecurringDonor;
    case "recurringDonationAmountGt":
      return AudienceTargetingFilterOperationGql.RecurringDonationAmountGt;
    case "recurringDonationAmountLt":
      return AudienceTargetingFilterOperationGql.RecurringDonationAmountLt;
    case "recurringDonationAmountBetween":
      return AudienceTargetingFilterOperationGql.RecurringDonationAmountBetween;
    case "hasDonated":
      return AudienceTargetingFilterOperationGql.HasDonated;
    case "notHasDonated":
      return AudienceTargetingFilterOperationGql.NotHasDonated;
    case "lastDonationGt":
      return AudienceTargetingFilterOperationGql.LastDonationGt;
    case "lastDonationLt":
      return AudienceTargetingFilterOperationGql.LastDonationLt;
    case "lastDonationBetween":
      return AudienceTargetingFilterOperationGql.LastDonationBetween;
    case "numberOfDonationsGt":
      return AudienceTargetingFilterOperationGql.NumberOfDonationsGt;
    case "numberOfDonationsLt":
      return AudienceTargetingFilterOperationGql.NumberOfDonationsLt;
    case "numberOfDonationsBetween":
      return AudienceTargetingFilterOperationGql.NumberOfDonationsBetween;
    case "averageDonationGt":
      return AudienceTargetingFilterOperationGql.AverageDonationGt;
    case "averageDonationLt":
      return AudienceTargetingFilterOperationGql.AverageDonationLt;
    case "averageDonationBetween":
      return AudienceTargetingFilterOperationGql.AverageDonationBetween;
    case "highestDonationGt":
      return AudienceTargetingFilterOperationGql.HighestDonationGt;
    case "highestDonationLt":
      return AudienceTargetingFilterOperationGql.HighestDonationLt;
    case "highestDonationBetween":
      return AudienceTargetingFilterOperationGql.HighestDonationBetween;
    case "hasMobileDonation":
      return AudienceTargetingFilterOperationGql.HasMobileDonation;
    case "notHasMobileDonation":
      return AudienceTargetingFilterOperationGql.NotHasMobileDonation;
    case "hasDonatedToCampaigns":
      return AudienceTargetingFilterOperationGql.HasDonatedToCampaigns;
    case "notHasDonatedToCampaigns":
      return AudienceTargetingFilterOperationGql.NotHasDonatedToCampaigns;
    case "hasReplied":
      return AudienceTargetingFilterOperationGql.HasReplied;
    case "notHasReplied":
      return AudienceTargetingFilterOperationGql.NotHasReplied;
    case "hasRepliedToCampaigns":
      return AudienceTargetingFilterOperationGql.HasRepliedToCampaigns;
    case "notHasRepliedToCampaigns":
      return AudienceTargetingFilterOperationGql.NotHasRepliedToCampaigns;
  }
}

function convertDateFilterKeyToGraphql(
  dateFilterKey: DateFilterKey
): DateFilterKeyGql {
  switch (dateFilterKey) {
    case "ALL_TIME":
      return DateFilterKeyGql.AllTime;
    case "CUSTOM_DATE_RANGE":
      return DateFilterKeyGql.CustomDateRange;
    case "LAST_MONTH":
      return DateFilterKeyGql.LastMonth;
    case "LAST_YEAR":
      return DateFilterKeyGql.LastYear;
    case "THIS_MONTH":
      return DateFilterKeyGql.ThisMonth;
    case "THIS_YEAR":
      return DateFilterKeyGql.ThisYear;
  }
}

const DEFAULT_FILTER_CONFIG: AudienceFilterConfig = {
  centerZips: [""],
  radius: "",
  searchBody: {},
};

function convertTargetingFormFilterConfigToGraphQL(
  filterConfig: AudienceFilterConfig
): AudienceTargetingInput["targetGroups"][number]["clauses"][number]["filterConfig"] {
  if (isEqual(filterConfig, DEFAULT_FILTER_CONFIG)) {
    return undefined;
  }
  return {
    ...omit(filterConfig, "__typename"),
    searchBody:
      filterConfig.searchBody &&
      Object.hasOwn(filterConfig.searchBody, "targetGroups")
        ? convertTargetingFormToGraphQL(
            filterConfig.searchBody as TargetingFormValues
          )
        : null,
  };
}

// Convert TargetingForm to GraphQL input format
export function convertTargetingFormToGraphQL(
  form: TargetingFormValues
): AudienceTargetingInput {
  return {
    targetGroups: form.targetGroups.map(group => ({
      clauses: group.clauses.map(clause => ({
        values: clause.values ?? undefined,
        value: clause.value ?? undefined,
        fieldType: convertFieldTypeToGraphql(clause.fieldType!),
        filterOperation: convertFilterOperationToGraphql(
          clause.filterOperation!
        ),
        filterConfig: clause.filterConfig
          ? convertTargetingFormFilterConfigToGraphQL(clause.filterConfig)
          : undefined,
        fieldName: clause.fieldName ?? undefined,
        filterCategory: convertFilterCategoryToGraphQL(clause.filterCategory),
      })),
      dateFilter: group.dateFilter
        ? {
            key: convertDateFilterKeyToGraphql(group.dateFilter.key),
            startDate:
              typeof group.dateFilter.startDate === "string"
                ? group.dateFilter.startDate
                : group.dateFilter.startDate?.toISO(),
            endDate:
              typeof group.dateFilter.endDate === "string"
                ? group.dateFilter.endDate
                : group.dateFilter.endDate?.toISO(),
          }
        : undefined,
    })),
  };
}

function convertFilterCategoryGqlToForm(
  filterCategoryGql: AudienceTargetingFilterCategoryGql
): AudienceTargetingFilterCategory {
  switch (filterCategoryGql) {
    case AudienceTargetingFilterCategoryGql.CustomField:
      return AudienceTargetingFilterCategory.CustomField;
    case AudienceTargetingFilterCategoryGql.DonationHistory:
      return AudienceTargetingFilterCategory.DonationHistory;
    case AudienceTargetingFilterCategoryGql.Location:
      return AudienceTargetingFilterCategory.Location;
    case AudienceTargetingFilterCategoryGql.SavedSearch:
      return AudienceTargetingFilterCategory.SavedSearch;
    case AudienceTargetingFilterCategoryGql.Engagement:
      return AudienceTargetingFilterCategory.Engagement;
  }
}

export function convertGraphQLToTargetingForm(
  audienceTargetingGqlOutput: AudienceTargetingGqlOutput
): AudienceTargeting {
  return {
    targetGroups: audienceTargetingGqlOutput.targetGroups.map(tg => ({
      ...tg,
      clauses: tg.clauses.map(clause => ({
        ...clause,
        filterCategory: convertFilterCategoryGqlToForm(clause.filterCategory),
        filterConfig: clause.filterConfig
          ? {
              ...clause.filterConfig,
              searchBody: clause.filterConfig.searchBody
                ? convertGraphQLToTargetingForm(clause.filterConfig.searchBody)
                : undefined,
            }
          : undefined,
      })),
    })),
  };
}

export const DEFAULT_TARGET: TargetingFormGroup = {
  clauses: [
    {
      filterCategory: AudienceTargetingFilterCategory.Location,
      filterOperation: null,
      fieldType: null,
      fieldName: null,
      values: [],
    },
  ],
};

type FilterConfig = {
  label: string;
  items: {
    label: string;
    value: AudienceTargetingFilterOperation;
    fieldType: AudienceTargetingFieldType;
    allowDateFiltering?: boolean;
  }[];
};

type FilterHierarchy = {
  [K in AudienceTargetingFilterCategory]: FilterConfig;
};

function customFieldOperations(
  customFields: GetAudienceContactFieldsQuery["getAudienceContactFields"]
): Record<string, FilterConfig["items"]> {
  return customFields
    .filter(field => field.isEditable)
    .reduce(
      (acc, field) => {
        switch (field.type) {
          case AudienceContactFieldType.String:
            acc[field.name] = [
              {
                label: "contains text",
                value: "contains",
                fieldType: "TEXT",
              },
              {
                label: "matches",
                value: "matches",
                fieldType: "TEXT",
              },
              {
                label: "does not contain text",
                value: "notContains",
                fieldType: "TEXT",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          case AudienceContactFieldType.Number:
            acc[field.name] = [
              {
                label: "is greater than",
                value: "gt",
                fieldType: field.type,
              },
              {
                label: "is less than",
                value: "lt",
                fieldType: field.type,
              },
              {
                label: "is equal to",
                value: "equals",
                fieldType: field.type,
              },
              {
                label: "is not equal to",
                value: "notEquals",
                fieldType: field.type,
              },
              {
                label: "is between",
                value: "between",
                fieldType: "NUMBER_RANGE",
              },
              {
                label: "is not between",
                value: "notBetween",
                fieldType: "NUMBER_RANGE",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          case AudienceContactFieldType.Boolean:
            acc[field.name] = [
              {
                label: "is true",
                value: "customTrue",
                fieldType: "BOOLEAN_TRUE",
              },
              {
                label: "is false",
                value: "customFalse",
                fieldType: "BOOLEAN_FALSE",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          case AudienceContactFieldType.Enum:
            acc[field.name] = [
              {
                label: "is one of",
                value: "oneOf",
                fieldType: "ENUM",
              },
              {
                label: "is not one of",
                value: "notOneOf",
                fieldType: "ENUM",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          case AudienceContactFieldType.State:
            acc[field.name] = [
              {
                label: "is in state",
                value: "isInCustomState",
                fieldType: "STATE",
              },
              {
                label: "is not in state",
                value: "notInCustomState",
                fieldType: "STATE",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          case AudienceContactFieldType.Zip:
            acc[field.name] = [
              {
                label: "is in zip code",
                value: "isInCustomZip",
                fieldType: "ZIP",
              },
              {
                label: "is not in zip code",
                value: "notInCustomZip",
                fieldType: "ZIP",
              },
              {
                label: "is in zip code radius",
                value: "isInCustomZipRadius",
                fieldType: "ZIP_RADIUS",
              },
              {
                label: "is not in zip code radius",
                value: "notInCustomZipRadius",
                fieldType: "ZIP_RADIUS",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          default:
            acc[field.name] = [];
        }
        return acc;
      },
      {} as Record<string, FilterConfig["items"]>
    );
}

function availableTargetingFilters({
  includeDonationHistory,
  includeCustomFields,
  includeSavedSearches,
}: {
  includeDonationHistory: boolean;
  includeCustomFields: boolean;
  includeSavedSearches: boolean;
}): Partial<FilterHierarchy> {
  const filterHierarchy: Partial<FilterHierarchy> = {
    location: {
      label: "Location",
      items: [
        {
          label: "is in state",
          value: "isInState",
          fieldType: "STATE",
        },
        {
          label: "is not in state",
          value: "notInState",
          fieldType: "STATE",
        },
        {
          label: "state is empty",
          value: "stateIsNull",
          fieldType: "IS_NULL",
        },
        {
          label: "is in zip code",
          value: "isInZip",
          fieldType: "ZIP",
        },
        {
          label: "is not in zip code",
          value: "notInZip",
          fieldType: "ZIP",
        },
        {
          label: "is in zip code radius",
          value: "isInZipRadius",
          fieldType: "ZIP_RADIUS",
        },
        {
          label: "is not in zip code radius",
          value: "notInZipRadius",
          fieldType: "ZIP_RADIUS",
        },
        {
          label: "zip is empty",
          value: "zipIsNull",
          fieldType: "IS_NULL",
        },
        {
          label: "is in area code",
          value: "isInAreaCode",
          fieldType: "AREA_CODE",
        },
        {
          label: "is not in area code",
          value: "notInAreaCode",
          fieldType: "AREA_CODE",
        },
      ],
    },
  };

  if (includeDonationHistory) {
    filterHierarchy.donationHistory = {
      label: "Donation History",
      items: [
        {
          label: "total contributions are greater than",
          value: "totalDonationGt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "total contributions are less than",
          value: "totalDonationLt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "total contributions are between range",
          value: "totalDonationBetween",
          fieldType: "CURRENCY_RANGE",
          allowDateFiltering: true,
        },
        {
          label: "is a recurring donor",
          value: "isRecurringDonor",
          fieldType: "BOOLEAN_TRUE",
        },
        {
          label: "is a recurring donor with recurring amount greater than",
          value: "recurringDonationAmountGt",
          fieldType: "CURRENCY",
        },
        {
          label: "is a recurring donor with recurring amount less than",
          value: "recurringDonationAmountLt",
          fieldType: "CURRENCY",
        },
        {
          label: "is a recurring donor with recurring amount between range",
          value: "recurringDonationAmountBetween",
          fieldType: "CURRENCY_RANGE",
        },
        {
          label: "is not a recurring donor",
          value: "notRecurringDonor",
          fieldType: "BOOLEAN_FALSE",
        },
        {
          label: "has made a donation",
          value: "hasDonated",
          fieldType: "BOOLEAN_TRUE",
          allowDateFiltering: true,
        },
        {
          label: "has not made a donation",
          value: "notHasDonated",
          fieldType: "BOOLEAN_FALSE",
          allowDateFiltering: true,
        },
        {
          label: "has made a donation on a mobile device",
          value: "hasMobileDonation",
          fieldType: "BOOLEAN_TRUE",
          allowDateFiltering: true,
        },
        {
          label: "has not made a donation on a mobile device",
          value: "notHasMobileDonation",
          fieldType: "BOOLEAN_FALSE",
          allowDateFiltering: true,
        },
        {
          label: "last donation was greater than",
          value: "lastDonationGt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "last donation was less than",
          value: "lastDonationLt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "last donation was between range",
          value: "lastDonationBetween",
          fieldType: "CURRENCY_RANGE",
          allowDateFiltering: true,
        },
        {
          label: "number of donations is greater than",
          value: "numberOfDonationsGt",
          fieldType: "NUMBER",
          allowDateFiltering: true,
        },
        {
          label: "number of donations is less than",
          value: "numberOfDonationsLt",
          fieldType: "NUMBER",
          allowDateFiltering: true,
        },
        {
          label: "number of donations is between range",
          value: "numberOfDonationsBetween",
          fieldType: "NUMBER_RANGE",
          allowDateFiltering: true,
        },
        {
          label: "average donation is greater than",
          value: "averageDonationGt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "average donation is less than",
          value: "averageDonationLt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "average donation is between range",
          value: "averageDonationBetween",
          fieldType: "CURRENCY_RANGE",
          allowDateFiltering: true,
        },
        {
          label: "highest donation is greater than",
          value: "highestDonationGt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "highest donation is less than",
          value: "highestDonationLt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "highest donation is between range",
          value: "highestDonationBetween",
          fieldType: "CURRENCY_RANGE",
          allowDateFiltering: true,
        },
        {
          label: "has made a donation from a campaign",
          value: "hasDonatedToCampaigns",
          fieldType: "CAMPAIGN",
          allowDateFiltering: true,
        },
        {
          label: "has not made a donation from a campaign",
          value: "notHasDonatedToCampaigns",
          fieldType: "CAMPAIGN",
          allowDateFiltering: true,
        },
      ],
    };
  }

  filterHierarchy.engagement = {
    label: "Engagement",
    items: [
      {
        label: "has replied",
        value: "hasReplied",
        fieldType: "BOOLEAN_TRUE",
        allowDateFiltering: true,
      },
      {
        label: "has not replied",
        value: "notHasReplied",
        fieldType: "BOOLEAN_FALSE",
        allowDateFiltering: true,
      },
      {
        label: "has replied to a campaign",
        value: "hasRepliedToCampaigns",
        fieldType: "CAMPAIGN",
        allowDateFiltering: true,
      },
      {
        label: "has not replied to a campaign",
        value: "notHasRepliedToCampaigns",
        fieldType: "CAMPAIGN",
        allowDateFiltering: true,
      },
    ],
  };

  if (includeCustomFields) {
    filterHierarchy.customField = {
      label: "Custom Field",
      items: [],
    };
  }

  if (includeSavedSearches) {
    filterHierarchy.savedSearch = {
      label: "Saved Search",
      items: [
        {
          label: "is in saved search audience",
          value: "isInSavedSearch",
          fieldType: "SAVED_SEARCH",
          allowDateFiltering: false,
        },
      ],
    };
  }

  return filterHierarchy;
}

const DATE_FILTER_RANGES = [
  { key: "ALL_TIME", label: "All time", start: null, end: null },
  {
    key: "LAST_YEAR",
    label: "Last year",
    start: DateTime.now().minus({ years: 1 }).startOf("year"),
    end: DateTime.now().minus({ years: 1 }).endOf("year"),
  },
  {
    key: "THIS_YEAR",
    label: "This year",
    start: DateTime.now().startOf("year"),
    end: DateTime.now().endOf("day"),
  },
  {
    key: "THIS_MONTH",
    label: "This month",
    start: DateTime.now().startOf("month"),
    end: DateTime.now().endOf("day"),
  },
  {
    key: "LAST_MONTH",
    label: "Last month",
    start: DateTime.now().minus({ months: 1 }).startOf("month"),
    end: DateTime.now().minus({ months: 1 }).endOf("month"),
  },
  {
    key: "CUSTOM_DATE_RANGE",
    label: "Specific date range",
    start: DateTime.now().minus({ days: 1 }).startOf("day"),
    end: DateTime.now().endOf("day"),
  },
];

const DATE_FILTER_RANGE_BY_KEY = keyBy(DATE_FILTER_RANGES, "key");

const FIELD_TYPE_TO_INPUT: {
  [key in AudienceTargetingFieldType]: {
    label?: string;
    nameSuffix: "value" | "values";
    bottomLabel?: string;
    slimVariant?: "underlined" | "outlined" | "standard";
    defaultVariant?: "underlined" | "outlined";
    slimSx?: SxProps<Theme>;
    defaultSx?: SxProps<Theme>;
    slimSlotProps?: TextFieldProps["slotProps"];
  };
} = {
  STATE: {
    label: "States",
    nameSuffix: "values",
    bottomLabel: "You may select multiple states.",
    slimVariant: "underlined",
    defaultVariant: "outlined",
    slimSx: {
      minWidth: "160px",
      fontSize: theme.spacing(1.5),
      maxWidth: "350px",
    },
  },
  NUMBER: {
    label: "Amount",
    nameSuffix: "value",
    bottomLabel: "Enter amount",
    slimVariant: "standard",
    slimSx: { minWidth: "160px" },
    slimSlotProps: {
      input: { sx: { fontSize: "12px", alignItems: "stretch" } },
    },
  },
  CURRENCY: {
    label: "Amount",
    nameSuffix: "value",
    bottomLabel: "Enter amount",
    slimVariant: "standard",
    slimSx: { minWidth: "160px" },
    slimSlotProps: {
      input: { sx: { fontSize: "12px", alignItems: "stretch" } },
    },
  },
  TEXT: {
    label: "Text Value",
    nameSuffix: "value",
    bottomLabel: "Enter text",
    slimVariant: "standard",
    slimSx: { minWidth: "160px", fontSize: theme.spacing(1.5) },
    slimSlotProps: {
      input: { sx: { fontSize: "12px", alignItems: "stretch" } },
    },
  },
  ZIP: {
    label: "Zip Codes",
    nameSuffix: "values",
    slimVariant: "standard",
    bottomLabel: "Enter zip codes separated by commas",
    slimSx: {
      minWidth: "200px",
      fontSize: theme.spacing(1.5),
      maxWidth: "350px",
    },
    slimSlotProps: {
      input: { sx: { fontSize: "12px" } },
    },
  },
  ZIP_RADIUS: {
    nameSuffix: "values",
  },
  ENUM: {
    label: "Value(s)",
    nameSuffix: "values",
    slimVariant: "underlined",
    slimSx: {
      minWidth: "120px",
      fontSize: theme.spacing(1.5),
      maxWidth: "350px",
    },
    defaultVariant: "outlined",
  },
  CAMPAIGN: {
    label: "Campaign(s)",
    nameSuffix: "values",
    slimVariant: "underlined",
    slimSx: {
      minWidth: "120px",
      fontSize: theme.spacing(1.5),
      maxWidth: "350px",
    },
    defaultVariant: "outlined",
  },
  CURRENCY_RANGE: {
    nameSuffix: "values",
    slimVariant: "standard",
    slimSx: { minWidth: "90px", fontSize: theme.spacing(1.5) },
    slimSlotProps: {
      input: { sx: { fontSize: "12px", alignItems: "stretch" } },
    },
  },
  NUMBER_RANGE: {
    nameSuffix: "values",
    slimVariant: "standard",
    slimSx: { minWidth: "100px", fontSize: theme.spacing(1.5) },
    slimSlotProps: {
      input: { sx: { fontSize: "12px", alignItems: "stretch" } },
    },
  },
  BOOLEAN: {
    nameSuffix: "value",
  },
  BOOLEAN_TRUE: {
    nameSuffix: "value",
  },
  BOOLEAN_FALSE: {
    nameSuffix: "value",
  },
  IS_NULL: {
    nameSuffix: "value",
  },
  SAVED_SEARCH: {
    label: "Saved Search Name",
    nameSuffix: "value",
    slimVariant: "underlined",
    defaultVariant: "outlined",
    slimSx: { minWidth: "160px", fontSize: theme.spacing(1.5) },
  },
  AREA_CODE: {
    label: "Area Codes",
    nameSuffix: "values",
    bottomLabel: "You may select multiple area codes.",
    slimVariant: "underlined",
    defaultVariant: "outlined",
    slimSx: {
      minWidth: "160px",
      fontSize: theme.spacing(1.5),
      maxWidth: "350px",
    },
  },
};

const BETWEEN_AUDIENCE_TARGETING_FILTER_OPERATIONS: Set<AudienceTargetingFilterOperation> =
  new Set([
    "averageDonationBetween",
    "between",
    "highestDonationBetween",
    "lastDonationBetween",
    "notBetween",
    "numberOfDonationsBetween",
    "recurringDonationAmountBetween",
    "totalDonationBetween",
  ]);

const BOOLEAN_AUDIENCE_TARGETING_FIELD_TYPES: Set<AudienceTargetingFieldType> =
  new Set(["BOOLEAN", "BOOLEAN_TRUE", "BOOLEAN_FALSE"]);

function dateTimeOrStringToDateTime(
  optionalDateTimeOrString: DateTime | string | null | undefined
): DateTime | undefined {
  if (!optionalDateTimeOrString) {
    return undefined;
  }

  if (typeof optionalDateTimeOrString === "string") {
    return DateTime.fromISO(optionalDateTimeOrString);
  }

  return optionalDateTimeOrString;
}

type SavedSearchFilter = {
  id: string;
  name: string;
  searchBody: AudienceTargeting;
};

function TargetingFilter({
  dateFilterLens,
  filterLens,
  handleRemove,
  audienceFields,
  savedSearchFilters,
  campaigns,
  allAvailableTargetingFilters,
  dateFilterReadOnly,
  timezone,
  variant = "default",
}: {
  dateFilterLens: Lens<TargetingDateFilter | null | undefined>;
  filterLens: Lens<TargetingFormFilter>;
  handleRemove: () => void;
  audienceFields: GetAudienceContactFieldsQuery["getAudienceContactFields"];
  savedSearchFilters: SavedSearchFilter[];
  campaigns: ListCampaignsQuery["listCampaigns"]["campaigns"];
  allAvailableTargetingFilters: Partial<FilterHierarchy>;
  dateFilterReadOnly?: boolean;
  timezone: string;
  variant?: "default" | "slim";
}): ReactElement {
  const { setValue, trigger, clearErrors } = useFormContext();

  const filterCategoryLens = filterLens.focus("filterCategory");
  const filterCategoryController = useController(filterCategoryLens.interop());

  const filterOperationLens = filterLens.focus("filterOperation");
  const filterOperationController = useController(
    filterOperationLens.interop()
  );

  const filterConfigLens = filterLens.focus("filterConfig");
  const filterConfigController = useController(filterConfigLens.interop());

  const fieldTypeLens = filterLens.focus("fieldType");
  const fieldTypeController = useController(fieldTypeLens.interop());

  const fieldNameLens = filterLens.focus("fieldName");
  const fieldNameController = useController(fieldNameLens.interop());

  const valueLens = filterLens.focus("value");
  const valueController = useController(valueLens.interop());

  const valuesLens = filterLens.focus("values");
  const valuesController = useController(valuesLens.interop());

  const searchBodyLens = filterLens.focus("filterConfig.searchBody");
  const searchBodyController = useController(searchBodyLens.interop());

  const dateFilterController = useController(dateFilterLens.interop());

  const availableOperations = useMemo(
    () =>
      (filterCategoryController.field.value === "customField"
        ? customFieldOperations(audienceFields)[
            fieldNameController.field.value!
          ]
        : allAvailableTargetingFilters[filterCategoryController.field.value]
            ?.items) || [],
    [
      filterCategoryController.field.value,
      audienceFields,
      fieldNameController.field.value,
      allAvailableTargetingFilters,
    ]
  );

  const selectedOperation = useMemo(
    () =>
      availableOperations.find(
        op => op.value === filterOperationController.field.value
      ),
    [availableOperations, filterOperationController.field.value]
  );

  useEffect(() => {
    if (
      valuesController.field.value?.find(
        v => !!v || (typeof v === "number" && v >= 0)
      ) &&
      valuesController.fieldState.isDirty
    ) {
      void trigger(valuesController.field.name);
    }
  }, [
    trigger,
    valuesController.field.name,
    valuesController.field.value,
    valuesController.fieldState.isDirty,
  ]);

  useEffect(() => {
    if (
      filterOperationController.field.value === "isInSavedSearch" &&
      valueController.field.value !== ""
    ) {
      const searchBody = savedSearchFilters.find(
        f => f.id === valueController.field.value
      )?.searchBody;

      setValue(searchBodyController.field.name, searchBody);
    }
  }, [
    valueController.field.value,
    filterOperationController.field.value,
    savedSearchFilters,
    setValue,
    searchBodyController.field.name,
  ]);

  const resetValues = useCallback(
    (
      newOperation: AudienceTargetingFilterOperation | null,
      shouldDirty = false
    ) => {
      setValue(valueController.field.name, "", {
        shouldDirty: false,
      });
      setValue(valuesController.field.name, [], { shouldDirty });

      const isRange =
        newOperation &&
        BETWEEN_AUDIENCE_TARGETING_FILTER_OPERATIONS.has(newOperation);
      if (isRange) {
        setValue(valuesController.field.name, ["", ""], { shouldDirty });
      }

      setValue(filterConfigController.field.name, DEFAULT_FILTER_CONFIG);

      clearErrors(valuesController.field.name);
    },
    [
      setValue,
      valueController,
      valuesController,
      filterConfigController,
      clearErrors,
    ]
  );

  function onFilterOperationChange(val: AudienceTargetingFilterOperation) {
    const newSelectedOperation = availableOperations.find(
      op => op.value === val
    );

    if (!newSelectedOperation) {
      setValue(filterOperationController.field.name, "");
      setValue(fieldTypeController.field.name, "");
      resetValues(val);
      return;
    }

    if (newSelectedOperation.fieldType) {
      setValue(fieldTypeController.field.name, newSelectedOperation.fieldType, {
        shouldDirty: true,
      });

      if (newSelectedOperation.fieldType === "IS_NULL") {
        setValue(valueController.field.name, "true", {
          shouldDirty: true,
          shouldValidate: true,
        });
      } else {
        resetValues(val, true);

        if (
          BOOLEAN_AUDIENCE_TARGETING_FIELD_TYPES.has(
            newSelectedOperation.fieldType
          )
        ) {
          setValue(
            valueController.field.name,
            newSelectedOperation.fieldType === "BOOLEAN_TRUE" ? true : false,
            { shouldDirty: true }
          );
        }
      }
    }

    if (
      newSelectedOperation.allowDateFiltering &&
      !dateFilterController.field.value?.key
    ) {
      setValue(
        dateFilterController.field.name,
        { key: "ALL_TIME", startDate: null, endDate: null },
        { shouldDirty: true }
      );
    }
  }

  const isSlim =
    variant === "slim" ||
    filterCategoryController.field.value === "savedSearch";

  function renderSelectedOperation() {
    if (!selectedOperation) {
      return null;
    }

    const fieldType = selectedOperation.fieldType;

    const coreProps = {
      ...filterLens.focus(FIELD_TYPE_TO_INPUT[fieldType]?.nameSuffix).interop(),
      fullWidth: !isSlim,
      bottomLabel: isSlim
        ? undefined
        : FIELD_TYPE_TO_INPUT[fieldType]?.bottomLabel,
      label: FIELD_TYPE_TO_INPUT[fieldType]?.label,
      sx: isSlim
        ? FIELD_TYPE_TO_INPUT[fieldType]?.slimSx
        : FIELD_TYPE_TO_INPUT[fieldType]?.defaultSx,
      slotProps: isSlim
        ? FIELD_TYPE_TO_INPUT[fieldType]?.slimSlotProps
        : undefined,
    };

    const inputVariant = isSlim
      ? FIELD_TYPE_TO_INPUT[fieldType]?.slimVariant
      : FIELD_TYPE_TO_INPUT[fieldType]?.defaultVariant;

    const enumValueOptions: string[] = fieldNameController.field.value
      ? (audienceFields.find(f => f.name === fieldNameController.field.value)
          ?.typeParameters?.values ?? [])
      : [];

    return (
      <>
        <input type="hidden" {...filterLens.focus("fieldType").interop()} />
        {selectedOperation?.allowDateFiltering &&
          dateFilterController.field.value?.key === "CUSTOM_DATE_RANGE" &&
          dateFilterController.field.value.endDate &&
          dateFilterController.field.value.startDate && (
            <FlexBox direction="row" gap={2}>
              <RhfDatePicker
                {...dateFilterLens.defined().focus("startDate").interop()}
                label="Start Date"
                fullWidth={!isSlim}
                timezone={timezone}
                disabled={dateFilterReadOnly}
                maxDate={dateTimeOrStringToDateTime(
                  dateFilterController.field.value.startDate
                )}
                isSlim={isSlim}
                backgroundColor={isSlim ? palette.lightGray : palette.white}
                inputLabelProps={
                  isSlim
                    ? {
                        sx: { mt: 1, mb: -1 },
                      }
                    : undefined
                }
              />
              <RhfDatePicker
                {...dateFilterLens.defined().focus("endDate").interop()}
                label="End Date"
                fullWidth={!isSlim}
                timezone={timezone}
                disabled={dateFilterReadOnly}
                minDate={dateTimeOrStringToDateTime(
                  dateFilterController.field.value.endDate
                )}
                isSlim={isSlim}
                backgroundColor={isSlim ? palette.lightGray : palette.white}
                inputLabelProps={
                  isSlim
                    ? {
                        sx: { mt: 1, mb: -1 },
                      }
                    : undefined
                }
              />
            </FlexBox>
          )}
        {selectedOperation.fieldType === "STATE" && (
          <RhfSelect
            {...coreProps}
            variant={inputVariant as "outlined" | "underlined"}
            multiple={true}
            renderValue={selectedValues => (
              <>{(selectedValues as string[]).join(", ")}</>
            )}
          >
            {STATES.map(state => (
              <MenuItem key={state.abbreviation} value={state.abbreviation}>
                <Checkbox
                  checked={(valuesController.field.value as string[]).includes(
                    state.abbreviation
                  )}
                  icon={<CheckBoxUnchecked />}
                />
                {state.name}
              </MenuItem>
            ))}
          </RhfSelect>
        )}
        {selectedOperation.fieldType === "AREA_CODE" && (
          <RhfSelect
            {...coreProps}
            variant={inputVariant as "outlined" | "underlined"}
            multiple={true}
            renderValue={selectedValues => (
              <>{(selectedValues as string[]).join(", ")}</>
            )}
          >
            {STATES_WITH_AREA_CODES.flatMap(state => [
              <ListSubheader
                key={`header-${state.abbreviation}`}
                sx={{ lineHeight: "32px", pt: 1 }}
              >
                {state.name}
              </ListSubheader>,
              ...state.areaCodes.map(areaCode => (
                <MenuItem key={areaCode} value={areaCode} sx={{ py: 0 }}>
                  <Checkbox
                    checked={(
                      valuesController.field.value as string[]
                    ).includes(areaCode)}
                    icon={<CheckBoxUnchecked />}
                  />
                  {areaCode}
                </MenuItem>
              )),
            ])}
          </RhfSelect>
        )}
        {selectedOperation.fieldType === "NUMBER" && (
          <RhfNumberField
            {...coreProps}
            variant={inputVariant as TextFieldProps["variant"]}
          />
        )}
        {selectedOperation.fieldType === "NUMBER_RANGE" && (
          <RhfNumericalRange
            lowLabel="Lower Limit"
            highLabel="Upper Limit"
            {...coreProps}
            variant={inputVariant as TextFieldProps["variant"]}
            bottomLabelSx={isSlim ? { mt: 0 } : {}}
          />
        )}
        {selectedOperation.fieldType === "CURRENCY" &&
          filterCategoryController.field.value === "donationHistory" && (
            <RhfCurrencyField
              {...coreProps}
              adornmentOffset={isSlim ? 0 : undefined}
              variant={inputVariant as TextFieldProps["variant"]}
            />
          )}
        {selectedOperation.fieldType === "TEXT" && (
          <RhfTextField
            {...coreProps}
            variant={inputVariant as TextFieldProps["variant"]}
          />
        )}
        {selectedOperation.fieldType === "ZIP" && (
          <RhfCommaSeparatedTextField
            placeholder="12345, 67890, 54321"
            {...coreProps}
            variant={inputVariant as TextFieldProps["variant"]}
          />
        )}
        {selectedOperation.fieldType === "ZIP_RADIUS" && (
          <RhfZipCodeRadius
            filterConfigLens={filterConfigLens.defined()}
            lookupResultsLens={valuesLens.defined().narrow<string[]>()}
            isSlim={isSlim}
          />
        )}
        {selectedOperation.fieldType === "CURRENCY_RANGE" && (
          <RhfNumericalRange
            lowLabel="Lower Limit"
            highLabel="Upper Limit"
            {...coreProps}
            variant={inputVariant as TextFieldProps["variant"]}
            inputSx={coreProps.sx}
            adornmentOffset={isSlim ? 0 : undefined}
            styleVariant="currency"
            bottomLabelSx={isSlim ? { mt: 0 } : {}}
          />
        )}
        {selectedOperation.fieldType.startsWith("BOOLEAN_") && (
          <input type="hidden" {...valueLens.interop()} />
        )}
        {selectedOperation.fieldType === "CAMPAIGN" && (
          <RhfSelect
            {...coreProps}
            variant={inputVariant as "outlined" | "underlined"}
            withNoneOption={false}
            multiple
            onChange={newValues => {
              if (_.isEqual(newValues, TARGETING_FILTER_ANY_VALUES)) {
                setValue(
                  valuesController.field.name,
                  TARGETING_FILTER_ANY_VALUES
                );
              } else {
                setValue(
                  valuesController.field.name,
                  (newValues as string[]).filter(
                    v => v !== TARGETING_FILTER_ANY_VALUES[0]
                  )
                );
              }
            }}
            renderValue={selectedValues => {
              if (_.isEqual(selectedValues, TARGETING_FILTER_ANY_VALUES)) {
                return <>Any campaign</>;
              }
              return (
                <>
                  {(selectedValues as string[])
                    .map(id => campaigns.find(c => c.id === id)?.name)
                    .filter(Boolean)
                    .join(", ")}
                </>
              );
            }}
          >
            <MenuItem
              key={TARGETING_FILTER_ANY_VALUES[0]}
              value={TARGETING_FILTER_ANY_VALUES[0]}
            >
              <Checkbox
                checked={_.isEqual(
                  valuesController.field.value,
                  TARGETING_FILTER_ANY_VALUES
                )}
                icon={<CheckBoxUnchecked />}
              />
              Any campaign
            </MenuItem>
            {campaigns.map(campaign => (
              <MenuItem key={campaign.id} value={campaign.id}>
                <Checkbox
                  checked={(valuesController.field.value as string[]).includes(
                    campaign.id
                  )}
                  icon={<CheckBoxUnchecked />}
                />
                {campaign.name}
              </MenuItem>
            ))}
          </RhfSelect>
        )}
        {selectedOperation.fieldType === "ENUM" && (
          <RhfSelect
            {...coreProps}
            variant={inputVariant as "outlined" | "underlined"}
            withNoneOption={false}
            multiple
            renderValue={selectedValues => (
              <>{(selectedValues as string[]).join(", ")}</>
            )}
          >
            {enumValueOptions.map(option => (
              <MenuItem key={option} value={option}>
                <Checkbox
                  checked={(valuesController.field.value as string[]).includes(
                    option
                  )}
                  icon={<CheckBoxUnchecked />}
                />
                {option}
              </MenuItem>
            ))}
          </RhfSelect>
        )}
        {selectedOperation.fieldType === "SAVED_SEARCH" && (
          <>
            <RhfSelect
              {...coreProps}
              variant={inputVariant as "outlined" | "underlined"}
            >
              {savedSearchFilters.map(search => (
                <MenuItem key={`menu-item-${search.id}`} value={search.id}>
                  {search.name}
                </MenuItem>
              ))}
            </RhfSelect>
            <input type="hidden" {...searchBodyLens.interop()} />
          </>
        )}
      </>
    );
  }

  return (
    <FlexBox direction="column" gap={2}>
      <FlexBox
        direction="row"
        gap={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: "100%" }}
      >
        <FlexBox
          gap={2}
          flexWrap="wrap"
          sx={{
            marginBottom: theme.spacing(1.5),
            "& .MuiInputLabel-root": {
              fontSize: theme.spacing(1.5),
            },
          }}
        >
          <Box>
            <RhfSelect
              {...filterCategoryLens.interop()}
              label="Filter Type"
              withNoneOption={false}
              sx={{ width: "140px", fontSize: theme.spacing(1.5) }}
              fullWidth
              variant="underlined"
              onChange={val => {
                if (val !== "customField") {
                  setValue(fieldNameController.field.name, "", {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }

                // Auto-set operation for savedSearch category
                if (val === "savedSearch") {
                  setValue(
                    filterOperationController.field.name,
                    "isInSavedSearch",
                    {
                      shouldDirty: true,
                      shouldValidate: true,
                    }
                  );
                  setValue(fieldTypeController.field.name, "SAVED_SEARCH", {
                    shouldDirty: true,
                  });
                } else {
                  setValue(filterOperationController.field.name, "");
                  setValue(fieldTypeController.field.name, "");
                }

                resetValues(null, true);
              }}
            >
              {Object.keys(allAvailableTargetingFilters).map(ft => (
                <MenuItem key={ft} value={ft}>
                  {
                    allAvailableTargetingFilters[
                      ft as AudienceTargetingFilterCategory
                    ]!.label
                  }
                </MenuItem>
              ))}
            </RhfSelect>
          </Box>
          {filterCategoryController.field.value === "customField" && (
            <Box>
              <RhfSelect
                {...fieldNameLens.interop()}
                label="Custom Field"
                withNoneOption={false}
                sx={{ minWidth: "140px", fontSize: theme.spacing(1.5) }}
                fullWidth
                variant="underlined"
                onChange={newFieldName => {
                  const selectedField = audienceFields.find(
                    f => f.name === newFieldName
                  );
                  if (selectedField) {
                    setValue(
                      fieldTypeController.field.name,
                      selectedField.type
                    );

                    resetValues(null, true);
                  }

                  setValue(filterOperationController.field.name, "");
                }}
              >
                {audienceFields
                  .filter(field => field.isEditable)
                  .map(field => (
                    <MenuItem key={field.id} value={field.name}>
                      {field.name}
                    </MenuItem>
                  ))}
              </RhfSelect>
            </Box>
          )}

          {filterCategoryController.field.value !== "savedSearch" && (
            <Box>
              <RhfSelect
                {...filterOperationLens.interop()}
                label="Operation"
                withNoneOption={false}
                sx={{ minWidth: "140px", fontSize: theme.spacing(1.5) }}
                fullWidth
                variant="underlined"
                onChange={newValue =>
                  onFilterOperationChange(
                    newValue as AudienceTargetingFilterOperation
                  )
                }
              >
                {availableOperations.map(op => (
                  <MenuItem key={op.label} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </RhfSelect>
            </Box>
          )}

          {selectedOperation?.allowDateFiltering && (
            <RhfSelect
              {...dateFilterLens.defined().focus("key").interop()}
              label="Date Range"
              withNoneOption={false}
              sx={{
                minWidth: "140px",
                fontSize: theme.spacing(1.5),
              }}
              variant="underlined"
              disabled={dateFilterReadOnly}
              onChange={newValue => {
                const { start, end } =
                  DATE_FILTER_RANGE_BY_KEY[newValue as string] || {};
                setValue(
                  dateFilterController.field.name,
                  { startDate: start, endDate: end, key: newValue },
                  { shouldDirty: true }
                );
              }}
            >
              {DATE_FILTER_RANGES.map(range => (
                <MenuItem key={range.key} value={range.key}>
                  {range.label}
                </MenuItem>
              ))}
            </RhfSelect>
          )}
          {isSlim && renderSelectedOperation()}
        </FlexBox>
        <Box>
          <LinkButton
            variant="body3"
            sx={{ color: isSlim ? palette.darkGray : undefined }}
            onClick={() => handleRemove()}
          >
            Remove Filter
          </LinkButton>
        </Box>
      </FlexBox>

      {!isSlim && renderSelectedOperation()}
    </FlexBox>
  );
}

function SavedSearchFilterTargetingSummary({
  targetGroups,
  campaignNamesById,
}: {
  targetGroups: TargetingFormGroup[];
  campaignNamesById: Record<string, string>;
}): ReactElement {
  const block = targetGroups.map((tg, i) => (
    <div key={`target-summary-${i}`}>
      <Typography variant="body2">
        {generateTargetGroupSentence(
          tg.clauses,
          campaignNamesById,
          "Subscribers who"
        )}
      </Typography>
      {targetGroups.length - 1 != i && (
        <Typography variant="body2">OR</Typography>
      )}
    </div>
  ));
  return <>{block}</>;
}

const AUDIENCE_TARGETING_FILTER_DONATION_HISTORY_OPERATIONS: Set<AudienceTargetingFilterOperation> =
  new Set([
    "totalDonationGt",
    "totalDonationLt",
    "totalDonationBetween",
    "isRecurringDonor",
    "notRecurringDonor",
    "recurringDonationAmountGt",
    "recurringDonationAmountLt",
    "recurringDonationAmountBetween",
    "hasDonated",
    "notHasDonated",
    "lastDonationGt",
    "lastDonationLt",
    "lastDonationBetween",
    "numberOfDonationsGt",
    "numberOfDonationsLt",
    "numberOfDonationsBetween",
    "averageDonationGt",
    "averageDonationLt",
    "averageDonationBetween",
    "highestDonationGt",
    "highestDonationLt",
    "highestDonationBetween",
    "hasMobileDonation",
    "notHasMobileDonation",
    "hasDonatedToCampaigns",
    "notHasDonatedToCampaigns",
  ]);

const AUDIENCE_TARGETING_FILTER_ENGAGEMENT_OPERATIONS: Set<AudienceTargetingFilterOperation> =
  new Set([
    "hasReplied",
    "notHasReplied",
    "hasRepliedToCampaigns",
    "notHasRepliedToCampaigns",
  ]);

function TargetGroup({
  lens,
  index,
  removeHandler,
  audienceFields,
  savedSearchFilters,
  campaigns,
  allAvailableTargetingFilters,
  timezone,
  variant = "default",
  objectLabel = "subscribers",
}: {
  lens: Lens<TargetingFormGroup>;
  index: number;
  removeHandler: () => void;
  audienceFields: GetAudienceContactFieldsQuery["getAudienceContactFields"];
  savedSearchFilters: SavedSearchFilter[];
  campaigns: ListCampaignsQuery["listCampaigns"]["campaigns"];
  allAvailableTargetingFilters: Partial<FilterHierarchy>;
  timezone: string;
  variant: "default" | "slim";
  objectLabel?: string;
}): ReactElement {
  const { setValue } = useFormContext();

  const clausesLens = lens.focus("clauses");
  const { fields, append, remove } = useFieldArray(clausesLens.interop());
  const clauses = useWatch(clausesLens.interop());

  const savedSearchFilterIndex = clauses.findIndex(
    clause => clause.filterCategory === "savedSearch"
  );

  const isSlim = variant === "slim";

  const targetSentence = useMemo(() => {
    if (clauses) {
      const sentence = generateTargetGroupSentence(
        clauses,
        Object.fromEntries(campaigns.map(c => [c.id, c.name])),
        `${objectLabel} who`
      );
      const prefix = isSlim ? "" : " - ";
      const formattedSentence = isSlim ? upperFirst(sentence) : sentence;
      return sentence.length > 0 ? prefix + formattedSentence : "";
    }
  }, [clauses, isSlim, objectLabel, campaigns]);

  const firstDateFilterIndex = clauses.findIndex(
    clause =>
      (clause.filterCategory === "donationHistory" &&
        clause.filterOperation &&
        AUDIENCE_TARGETING_FILTER_DONATION_HISTORY_OPERATIONS.has(
          clause.filterOperation
        )) ||
      (clause.filterCategory === "engagement" &&
        clause.filterOperation &&
        AUDIENCE_TARGETING_FILTER_ENGAGEMENT_OPERATIONS.has(
          clause.filterOperation
        ))
  );

  useEffect(() => {
    // If the last date-based filter is removed, clear out the form value
    if (firstDateFilterIndex < 0) {
      setValue(`targetGroups.${index}.dateFilter`, null);
    }
  }, [firstDateFilterIndex, setValue, index]);

  const leftPadding = isSlim ? 2 : 3;

  return (
    <FlexBox
      direction="column"
      sx={isSlim ? { border: `1px solid ${palette.midGray}` } : {}}
    >
      <FlexBox
        justifyContent="space-between"
        sx={{
          backgroundColor: palette.midGray,
        }}
      >
        <Typography variant="body2" sx={{ p: 1, pl: leftPadding }}>
          <strong style={{ fontWeight: isSlim ? 600 : undefined }}>
            Filter Group {index + 1}
          </strong>{" "}
          - {objectLabel} who meet all of the following filters
        </Typography>
        <IconButton onClick={removeHandler} aria-label="delete target">
          <Delete sx={{ height: "16px" }} />
        </IconButton>
      </FlexBox>
      {clausesLens.map(fields, (clause, clauseLens, idx) => (
        <FlexBox
          key={clause.id}
          flexDirection="column"
          sx={{
            backgroundColor: palette.lightGray,
            p: 1.4,
            pl: leftPadding,
            marginBottom: isSlim ? undefined : "2px",
            borderBottom: isSlim ? `1px solid ${palette.midGray}` : undefined,
          }}
        >
          <TargetingFilter
            dateFilterLens={lens.focus("dateFilter")}
            filterLens={clauseLens}
            handleRemove={() => remove(idx)}
            audienceFields={audienceFields}
            savedSearchFilters={savedSearchFilters}
            campaigns={campaigns}
            allAvailableTargetingFilters={
              savedSearchFilterIndex < 0
                ? idx > 0
                  ? omit(allAvailableTargetingFilters, "savedSearch")
                  : allAvailableTargetingFilters
                : savedSearchFilterIndex === idx
                  ? allAvailableTargetingFilters
                  : omit(allAvailableTargetingFilters, "savedSearch")
            }
            dateFilterReadOnly={
              firstDateFilterIndex > -1
                ? firstDateFilterIndex !== idx &&
                  firstDateFilterIndex !== undefined
                : false
            }
            timezone={timezone}
            variant={variant}
          />
        </FlexBox>
      ))}

      {savedSearchFilterIndex > -1 ? (
        <FlexBox
          direction="column"
          gap={1}
          sx={{
            backgroundColor: palette.lightGray,
            padding: theme.spacing(1.4, leftPadding),
            marginBottom: isSlim ? undefined : "2px",
            fontSize: theme.spacing(1.5),
            minWidth: "auto",
            height: isSlim ? "30px" : undefined,
          }}
        >
          <SavedSearchFilterTargetingSummary
            targetGroups={
              clauses[0].filterConfig?.searchBody?.targetGroups ?? []
            }
            campaignNamesById={Object.fromEntries(
              campaigns.map(c => [c.id, c.name])
            )}
          />
        </FlexBox>
      ) : (
        <FlexBox
          gap={2}
          alignItems="center"
          flexDirection={isSlim ? "row-reverse" : "row"}
          justifyContent={isSlim ? "space-between" : undefined}
          sx={{
            backgroundColor: palette.lightGray,
            padding: theme.spacing(1.4, leftPadding),
            marginBottom: isSlim ? undefined : "2px",
          }}
        >
          <Button
            variant="contained"
            color="tertiary"
            sx={{
              fontSize: isSlim ? "10px" : "12px",
              backgroundColor: palette.midGray,
              height: isSlim ? "30px" : undefined,
              minWidth: "auto",
              px: isSlim ? 1 : 2,
            }}
            onClick={() =>
              append(
                {
                  filterCategory: AudienceTargetingFilterCategory.Location,
                  filterOperation: null,
                  fieldType: null,
                  values: [],
                },
                {
                  shouldFocus: false,
                }
              )
            }
          >
            + Add Filter
          </Button>
          {targetSentence && (
            <Typography variant="body2">
              {!isSlim && <strong>Filter Group {index + 1}</strong>}{" "}
              {targetSentence}
            </Typography>
          )}
        </FlexBox>
      )}
    </FlexBox>
  );
}

export function TargetGroups({
  lens,
  fieldArray,
  audienceId,
  timezone,
  variant = "default",
  savedSearchesEnabled = false,
  objectLabel = "subscribers",
}: {
  lens: Lens<TargetingFormGroup[]>;
  fieldArray: UseFieldArrayReturn<
    HookFormControlShim<TargetingFormGroup[]>,
    "__DO_NOT_USE_HOOK_FORM_CONTROL_SHIM__"
  >;
  audienceId: string;
  timezone: string;
  variant?: "default" | "slim";
  savedSearchesEnabled?: boolean;
  objectLabel?: string;
}): ReactElement {
  const { subaccount, donationHistoryPageEnabled } = useSubaccount()!;

  const includeDonationHistory = donationHistoryPageEnabled || false;
  const includeCustomFields = useFeatureFlag("audienceCustomFields");

  const allAvailableTargetingFilters = availableTargetingFilters({
    includeDonationHistory,
    includeCustomFields,
    includeSavedSearches: savedSearchesEnabled,
  });

  const {
    data: customFields,
    loading,
    error,
  } = useQuery<
    GetAudienceContactFieldsQuery,
    GetAudienceContactFieldsQueryVariables
  >(GET_AUDIENCE_CONTACT_FIELDS, {
    variables: {
      input: {
        audienceId,
      },
    },
    skip: !includeCustomFields,
  });

  const {
    data: savedSearchesData,
    loading: savedSearchesLoading,
    error: savedSearchesError,
  } = useQuery<
    ListAudienceSearchFiltersQuery,
    ListAudienceSearchFiltersQueryVariables
  >(LIST_AUDIENCE_SEARCH_FILTERS, {
    variables: {
      input: {
        audienceId,
        subaccountId: subaccount.id,
        orderBy: "NAME_ASC",
      },
    },
    skip: !audienceId || !savedSearchesEnabled,
  });

  const {
    campaigns,
    loading: campaignsLoading,
    error: campaignsError,
  } = useCampaignNamesById({
    filter: {
      statusIn: [CampaignStatus.InitialSendComplete],
    },
  });

  const savedSearchFilters: SavedSearchFilter[] = useMemo(
    () =>
      savedSearchesData?.listAudienceSearchFilters.audienceSearchFilters.map(
        filter => ({
          id: filter.id,
          name: filter.name,
          searchBody: convertGqlAudienceTargetingToFormValues(
            filter.searchBody
          ),
        })
      ) ?? [],
    [savedSearchesData]
  );

  return (
    <AsyncWrapper
      loading={[loading, savedSearchesLoading, campaignsLoading]}
      errors={[error, savedSearchesError, campaignsError]}
    >
      <FlexBox direction="column" sx={{ marginBottom: theme.spacing(3) }}>
        {lens.map(fieldArray.fields, (targetGroup, targetGroupLens, index) => {
          return (
            <div key={targetGroup.id}>
              {index > 0 && (
                <Typography
                  variant="body1Bold"
                  sx={{ margin: theme.spacing(2, 0) }}
                  component={"div"}
                >
                  OR...
                </Typography>
              )}
              <TargetGroup
                lens={targetGroupLens}
                index={index}
                removeHandler={() => fieldArray.remove(index)}
                allAvailableTargetingFilters={allAvailableTargetingFilters}
                audienceFields={customFields?.getAudienceContactFields || []}
                savedSearchFilters={savedSearchFilters}
                campaigns={campaigns}
                timezone={timezone}
                variant={variant}
                objectLabel={objectLabel}
              />
            </div>
          );
        })}
      </FlexBox>
    </AsyncWrapper>
  );
}
